/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/AiAssistantButton.tsx
 * PURPOSE: Teal “AI Assistant” control pinned at the top of admin sidebars (tenant + super); toggles Langflow panel.
 * RECENT: 2026-04-27 — [2.2.705] **`onKeyDown`** (**Enter**) calls **`onClick`** for keyboard parity (jsdom + Vitest **`AiAssistantButton.test.tsx`**); root **`CHANGELOG` [2.2.705]**.
 */

import { SparklesIcon } from '@heroicons/react/24/outline';

export interface AiAssistantButtonProps {
  collapsed?: boolean;
  onClick: () => void;
  isOpen?: boolean;
}

export function AiAssistantButton({ collapsed, onClick, isOpen }: AiAssistantButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-expanded={isOpen}
      aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
      aria-controls="brazn-ai-assistant-panel"
      className={[
        'flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-white shadow-sm transition-colors',
        'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300',
        'dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800',
        isOpen ? 'ring-2 ring-primary-200 ring-offset-2 ring-offset-gray-50 dark:ring-primary-500 dark:ring-offset-gray-800' : '',
        collapsed ? 'justify-center px-2' : 'gap-3',
      ].join(' ')}
    >
      <SparklesIcon className="h-5 w-5 shrink-0 text-white" aria-hidden />
      {!collapsed && <span className="truncate">AI Assistant</span>}
    </button>
  );
}
